import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Share, Search, Eye, Calendar, Lock, FileText } from "lucide-react";
import { useState } from "react";
import { GenerateReportDialog, type ReportConfig } from "@/components/portfolio/GenerateReportDialog";
import { ReportViewer } from "@/components/portfolio/ReportViewer";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Reports will be stored in database once the reports table is created
// For now, show empty state with ability to generate new reports

export default function Packs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [viewerConfig, setViewerConfig] = useState<ReportConfig | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  // Empty array - will be replaced with database query when reports table exists
  const reports: Array<{
    id: string;
    title?: string;
    type?: string;
    status: string;
    views: number;
    watermark?: string;
    generatedAt?: string;
    generatedBy?: string;
    expiresAt: string;
    size?: string;
  }> = [];

  const filteredReports = reports.filter(report =>
    report.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeReports = reports.filter(r => r.status === "active").length;
  const totalViews = reports.reduce((sum, r) => sum + (r.views || 0), 0);

  const getStatusBadge = (status: string) => {
    if (status === "active") {
      return <Badge variant="default" className="gap-1"><Eye className="h-3 w-3" />Active</Badge>;
    }
    return <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" />Expired</Badge>;
  };

  const isExpiringSoon = (expiresAt: string) => {
    const daysUntilExpiry = Math.floor(
      (new Date(expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  };

  const handleGenerateReport = (config: ReportConfig) => {
    setViewerConfig(config);
    setViewerOpen(true);
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            Generate and share portfolio reports with watermarks and expiry
          </p>
        </div>
        <Button size="sm" onClick={() => setGenerateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Generate Report
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reports.length || 'N/A'}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reports.length > 0 ? activeReports : 'N/A'}</div>
            <p className="text-xs text-muted-foreground">Not expired</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reports.length > 0 ? totalViews : 'N/A'}</div>
            <p className="text-xs text-muted-foreground">Across all reports</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">N/A</div>
            <p className="text-xs text-muted-foreground">Generated this month</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Reports</CardTitle>
              <CardDescription>
                Track views, share with expiry dates, and download
              </CardDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 w-[250px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredReports.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{report.title}</p>
                        <p className="text-xs text-muted-foreground">Watermark: {report.watermark}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{report.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{report.generatedAt}</p>
                        <p className="text-xs text-muted-foreground">by {report.generatedBy}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{report.expiresAt}</p>
                          {isExpiringSoon(report.expiresAt) && report.status === "active" && (
                            <Badge variant="outline" className="text-xs">Expiring soon</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{report.views}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{report.size}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {report.status === "active" && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Share className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Reports Generated</h3>
              <p className="text-muted-foreground max-w-sm mb-4">
                Generate your first portfolio report to share with stakeholders.
              </p>
              <Button onClick={() => setGenerateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Generate Report
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <GenerateReportDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        onGenerate={handleGenerateReport}
      />

      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto p-6">
          {viewerConfig && (
            <ReportViewer
              config={viewerConfig}
              onClose={() => setViewerOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
